import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { ComboboxFK } from './ComboboxFK';
import { getUploadUrl } from '@/lib/utils';
import type { FKOption } from '@/hooks/useFKLookup';

export type EntitySelectType =
  | 'customers'
  | 'products'
  | 'materials'
  | 'orders'
  | 'quotations'
  | 'productionOrders'
  | 'config_materialTypes'
  | 'config_processes';

interface EntitySelectProps {
  entityType: EntitySelectType;
  value: string;
  onChange: (id: string, option?: FKOption) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  hideLabel?: boolean;
  onNameChange?: (name: string) => void;
  quickAddLabel?: string;
  onQuickAddClick?: (name: string) => void;
  canAdd?: boolean;
  
  // Custom query variables (like customerId for products)
  params?: Record<string, any>;
}

export function EntitySelect({
  entityType,
  value,
  onChange,
  label,
  placeholder,
  required,
  disabled,
  error,
  hideLabel,
  onNameChange,
  quickAddLabel,
  onQuickAddClick,
  canAdd,
  params,
}: EntitySelectProps) {
  const [search, setSearch] = React.useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['entity-select', entityType, search, params],
    queryFn: async () => {
      const input = { search, ...params };
      switch (entityType) {
        case 'customers':
          return trpcClient.customers.getSelect.query(input);
        case 'products':
          return trpcClient.products.getSelect.query(input);
        case 'materials':
          return trpcClient.materials.getSelect.query(input);
        case 'orders':
          return trpcClient.orders.getSelect.query(input);
        case 'quotations':
          return trpcClient.quotations.getSelect.query(input);
        case 'productionOrders':
          return trpcClient.productionOrders.getSelect.query(input);
        case 'config_materialTypes':
          return trpcClient.config.getSelectMaterialTypes.query(input);
        case 'config_processes':
          return trpcClient.config.getSelectProcesses.query(input);
        default:
          return [];
      }
    },
    staleTime: 10 * 1000,
    gcTime: 60 * 1000,
  });

  const options = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map((item: any) => {
      let optLabel = item.name || item.code || item.id;
      let optMeta = '';
      let searchTags = [item.code, item.name].filter(Boolean).join(' ').toLowerCase();

      switch (entityType) {
        case 'customers':
          optLabel = item.shortName || item.name;
          optMeta = [
            item.name !== item.shortName ? item.name : null,
            item.taxCode ? `MST: ${item.taxCode}` : null,
            item.phone ? `📞 ${item.phone}` : null,
          ].filter(Boolean).join(' · ');
          searchTags = [item.code, item.name, item.shortName, item.taxCode, item.phone].filter(Boolean).join(' ').toLowerCase();
          break;
        case 'products':
          optLabel = item.name || item.productName || item.productCode;
          optMeta = [item.productCode, item.customer?.shortName || item.customer?.name || item.customerName].filter(Boolean).join(' · ');
          searchTags = [item.productCode, item.productName, item.name, item.customer?.name, item.customer?.shortName].filter(Boolean).join(' ').toLowerCase();
          break;
        case 'materials':
          optLabel = item.name || item.materialId;
          const size = (item.width || item.length) ? `${item.width || '?'}×${item.length || '?'}mm` : '';
          const gsm = item.gsm ? `${item.gsm}gsm` : '';
          optMeta = [size, gsm, item.category].filter(Boolean).join(' · ');
          searchTags = [item.materialId, item.name, item.category].filter(Boolean).join(' ').toLowerCase();
          break;
        case 'orders':
          optLabel = item.orderNo;
          optMeta = item.customerName;
          break;
        case 'quotations':
          optLabel = item.quotationNo;
          optMeta = item.customerName;
          break;
        case 'productionOrders':
          optLabel = item.poNo;
          optMeta = item.productName;
          searchTags = [item.poNo, item.productName, item.productCode].filter(Boolean).join(' ').toLowerCase();
          break;
      }

      return {
        id: item.id,
        label: optLabel,
        meta: optMeta,
        searchTags,
        rawData: item,
      };
    });
  }, [data, entityType]);

  return (
    <ComboboxFK
      value={value}
      onChange={(id) => {
        const option = options.find((o) => o.id === id);
        onChange(id, option);
      }}
      options={options}
      label={label}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      error={error}
      hideLabel={hideLabel}
      onNameChange={onNameChange}
      quickAddLabel={quickAddLabel}
      onQuickAddClick={onQuickAddClick}
      canAdd={canAdd}
      onSearchChange={setSearch}
      loading={isLoading}
    />
  );
}
